import { TableColumnSizingOptions } from "@fluentui/react-components";
import { useCallback, useMemo, useState } from "react";

const STORAGE_PREFIX = "vsa-admin:columnWidths:";

type ColumnWidthsMap = Record<string, number>;

const readSavedWidths = (storageKey: string): ColumnWidthsMap => {
  if (!storageKey || typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") {
      return parsed as ColumnWidthsMap;
    }
    return {};
  } catch {
    return {};
  }
};

const writeSavedWidths = (storageKey: string, widths: ColumnWidthsMap) => {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${storageKey}`,
      JSON.stringify(widths)
    );
  } catch {
    /* ignore quota or serialization errors */
  }
};

const clearSavedWidths = (storageKey: string) => {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
  } catch {
    /* ignore */
  }
};

export interface UseColumnWidthsResult {
  /** Column sizing options merged from defaults + persisted user widths. */
  columnSizingOptions: TableColumnSizingOptions;
  /** Callback to wire into Fluent DataGrid `onColumnResize`. */
  handleColumnResize: (columnId: string, width: number) => void;
  /** Clears any persisted widths for this storage key. */
  resetColumnWidths: () => void;
  /**
   * Increments each time widths are reset. Pass as `key` to GenericDataGrid
   * so the grid remounts and picks up the default column widths.
   */
  gridKey: number;
}

/**
 * Loads any user-saved column widths for the given storageKey from localStorage
 * and merges them on top of the supplied defaults. Returns a stable resize
 * handler that persists new widths back to storage.
 */
export const useColumnWidths = (
  storageKey: string | undefined,
  defaultSizingOptions: TableColumnSizingOptions
): UseColumnWidthsResult => {
  const [savedWidths, setSavedWidths] = useState<ColumnWidthsMap>(() =>
    storageKey ? readSavedWidths(storageKey) : {}
  );
  const [gridKey, setGridKey] = useState(0);

  const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => {
    const merged: TableColumnSizingOptions = { ...defaultSizingOptions };

    Object.entries(savedWidths).forEach(([columnId, width]) => {
      merged[columnId] = {
        ...(merged[columnId] ?? {}),
        idealWidth: width,
        defaultWidth: width,
      };
    });

    return merged;
  }, [defaultSizingOptions, savedWidths]);

  const handleColumnResize = useCallback(
    (columnId: string, width: number) => {
      if (!storageKey || !columnId || !width || width <= 0) {
        return;
      }

      setSavedWidths((current) => {
        if (current[columnId] === width) {
          return current;
        }
        const next = { ...current, [columnId]: width };
        writeSavedWidths(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const resetColumnWidths = useCallback(() => {
    if (!storageKey) {
      return;
    }
    clearSavedWidths(storageKey);
    setSavedWidths({});
    setGridKey((k) => k + 1);
  }, [storageKey]);

  return {
    columnSizingOptions,
    handleColumnResize,
    resetColumnWidths,
    gridKey,
  };
};
